import _ from 'underscore';
import React, { useMemo, useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Divider,
  mergeClasses,
} from '@fluentui/react-components';

import {
  getAllExpandStepIds,
  parseStepConfig,
  getDefaultItemIcon,
  getDefaultItemAriaLabel,
} from './utils';
import { useStepProgressListStyle } from './styles/step-progress-list-style';

interface StepProgressListProps {
  classes?: {
    root?: string;
  };
  config: Array<{
    id: string;
    title?: string;
    detail?: string;
    steps: Array<{
      id: string;
      title: string;
      detail?: string;
    }>;
  }>;
  currentStepId: string;
  currentFlow?: Array<string>;
  getItemIcon?: (step: any) => string;
  getOnItemClickFn?: (step: any) => any;
  getItemAriaLabel?: (step: any) => string;
  isHorizontal?: boolean;
  isTitleCollapsed?: boolean;
}

export const StepProgressList: React.FC<StepProgressListProps> = (props) => {
  const {
    config, currentStepId, getItemIcon, getOnItemClickFn, getItemAriaLabel, isHorizontal, isTitleCollapsed,
  } = props;
  const i18n = useContext(I18nContext);
  const classes = useStepProgressListStyle();

  const currentFlow = useMemo(() => {
    if (props.currentFlow) {
      return props.currentFlow;
    }
    return getAllExpandStepIds(config);
  }, [config, props.currentFlow]);

  const steps = useMemo(() => parseStepConfig(config, currentStepId, currentFlow), [config, currentFlow, currentStepId]);

  const stepListClassNames = _.compact([
    isHorizontal ? 'horizontal-step-list' : 'vertical-step-list',
    isHorizontal ? classes.listContainerHorizontal : classes.listContainerVertical,
    isTitleCollapsed && classes.listContainerCollapsed,
    props.classes?.root,
  ]);

  return (
    <div className={mergeClasses(...stepListClassNames)}>
      {_.map(steps, (step) => {
        if (!step.isEnabled || step.isHidden) {
          return null;
        }
        const listItemStatusClassNames = _.compact([
          classes.stepListItem,
          isHorizontal ? classes.stepListItemHorizontal : classes.stepListItemVertical,
          step.isSubStep && 'step-list-item-sub-step',
          step.isVisited && 'step-list-item-visited',
          step.isBehind && 'step-list-item-behind',
          step.isAhead && 'step-list-item-ahead',
          step.isSelected && 'step-list-item-selected',
          step.containsSelected && 'step-list-item-contains-selected',
          step.hasError && 'step-list-item-error',
          step.containsErrorStep && 'step-list-item-contains-error',
        ]);

        const listItemDividerStatusClassNames = _.compact([
          classes.stepListItemDivider,
          isHorizontal ? classes.stepListItemDividerHorizontal : classes.stepListItemDividerVertical,
          step.isBehind && classes.stepListItemDividerBehind,
          (step.hasError || step.containsErrorStep) && classes.stepListItemDividerError,
        ]);

        const listItemIconStatusClassNames = _.compact([
          classes.stepListItemIcon,
          isHorizontal ? classes.stepListItemIconHorizontal : classes.stepListItemIconVertical,
          step.isSubStep && classes.stepListItemIconSubStep,
          step.isBehind && classes.stepListItemIconBehind,
          (step.isSelected || step.containsSelected) && classes.stepListItemIconSelected,
          (step.hasError || step.containsErrorStep) && classes.stepListItemIconError,
        ]);

        const stepListItemTitleContainerClassNames = _.compact([
          isHorizontal ? classes.stepListItemTitleContainerHorizontal : classes.stepListItemTitleContainerVertical,
          step.isSubStep && (isHorizontal ? classes.stepListItemTitleContainerSubStepHorizontal : classes.stepListItemTitleContainerSubStepVertical),
          isTitleCollapsed && classes.stepListItemTitleContainerCollapsed,
        ]);

        const icon = getItemIcon ? getItemIcon(step) : getDefaultItemIcon(step);

        const onItemClick = getOnItemClickFn ? getOnItemClickFn(step) : undefined;
        const onItemKeyDown = onItemClick ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onItemClick();
          }
        } : undefined;

        const itemAriaLabel = getItemAriaLabel ? getItemAriaLabel(step) : getDefaultItemAriaLabel(step, i18n);
        const a11yProps = onItemClick ? {
          role: 'button',
          tabIndex: 0,
          onClick: onItemClick,
          onKeyDown: onItemKeyDown,
          'aria-label': itemAriaLabel,
        } : {};

        return (
          <div key={step.id} className={mergeClasses('step-list-item', `step-list-item-${step.id}`, ...listItemStatusClassNames)}>
            <div
              className={mergeClasses('step-list-item-content', `step-list-item-content-${step.id}`, classes.stepListItemContent)}
              {...a11yProps}
            >
              <div className={mergeClasses('step-list-item-icon', ...listItemIconStatusClassNames)}>
                {icon}
              </div>
              <div className={mergeClasses('step-list-item-title-container', ...stepListItemTitleContainerClassNames)}>
                <div
                  className={mergeClasses('step-list-item-title', classes.stepListItemTitle, isHorizontal ? classes.stepListItemTitleHorizontal : classes.stepListItemTitleVertical, step.isSubStep && classes.stepListItemTitleSubStep)}
                  title={step.title}
                >
                  {step.title}
                </div>
                {step.detail && <div className={mergeClasses('step-list-item-detail', classes.stepListItemDetail, step.isSubStep && classes.stepListItemDetailSubStep)} title={step.detail}>{step.detail}</div>}
              </div>
            </div>
            <Divider className={mergeClasses('step-list-item-divider', ...listItemDividerStatusClassNames)} vertical={!isHorizontal} />
          </div>
        );
      })}
    </div>
  );
};

StepProgressList.defaultProps = {
  currentFlow: undefined,
  getItemIcon: undefined,
  getOnItemClickFn: undefined,
  getItemAriaLabel: undefined,
  isHorizontal: false,
  isTitleCollapsed: false,
};
